'use client'

import { useSelector, useDispatch } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import { AppDispatch } from '@/store'
import {
  selectCurrentUser,
  selectIsAuthenticated,
  selectUserLoading,
  selectUserError,
  selectAuthCheckCompleted,
  setUser,
  clearUser,
  setLoading,
  setError,
  User,
} from '@/store/user/slice'
import { SESSION_STORAGE_KEYS, LOCAL_STORAGE_KEYS } from '@/constants/storageKeys'
// import { MembersService } from '@/lib/api/services/members'

// Global flag to prevent concurrent auth checks across all component instances
let isAuthCheckInProgress = false
// Global flag to prevent multiple logout calls
let isLogoutInProgress = false
// Global flag to prevent multiple cleanup calls
let hasCleanedUpBeforePath = false

// BEFORE_PATH削除の例外パス（ログイン・登録フロー中は削除しない）
const BEFORE_PATH_EXCEPTION_PATHS = [
  '/login',
  '/signup',
  '/members/activate/',
  '/members/additional_info',
  '/members/mypage',
]

// 期限切れのBEFORE_PATHをlocalStorageから削除
const cleanupExpiredBeforePath = () => {
  if (hasCleanedUpBeforePath) return
  hasCleanedUpBeforePath = true

  if (typeof window === 'undefined') return

  // ログイン・登録フロー中のページでは削除しない
  const currentPath = window.location.pathname
  const isExceptionPath = BEFORE_PATH_EXCEPTION_PATHS.some((path) => currentPath.startsWith(path))
  if (isExceptionPath) return

  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.BEFORE_PATH)
    if (!stored) return

    try {
      const data = JSON.parse(stored)
      // 期限が設定されている場合はチェック
      if (data && typeof data === 'object' && 'expiry' in data) {
        if (Date.now() > data.expiry) localStorage.removeItem(LOCAL_STORAGE_KEYS.BEFORE_PATH)
      }
    } catch {
      // 文字列形式の場合は何もしない
    }
  } catch {
    // SecurityError発生時は無視
  }
}

export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>()
  const currentUser = useSelector(selectCurrentUser)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectUserLoading)
  const error = useSelector(selectUserError)
  const authCheckCompleted = useSelector(selectAuthCheckCompleted)
  const [isInitialized, setIsInitialized] = useState(false)

  // sessionStorageからメンバーIDを取得
  const getMemberId = useCallback((): number | null => {
    if (typeof window === 'undefined') return null

    try {
      const memberData = sessionStorage.getItem(SESSION_STORAGE_KEYS.MEMBER)
      if (!memberData) return null

      const member = JSON.parse(memberData)
      return member.id ? parseInt(member.id, 10) : null
    } catch {
      // SecurityError or JSON parse error
      return null
    }
  }, [])

  // 認証状態の初期化 (Vue.js版のMixinMemberIdと同様の動作 + OAuth対応)
  const initializeAuth = useCallback(async () => {
    if (typeof window === 'undefined') {
      return
    }

    // Skip if auth check already completed (cached in Redux)
    if (authCheckCompleted) {
      setIsInitialized(true)
      return
    }

    // Skip if auth check is in progress globally
    if (isAuthCheckInProgress) {
      return
    }

    // Mark as in progress
    isAuthCheckInProgress = true
    dispatch(setLoading(true))

    try {
      let member: string | null = null
      try {
        member = sessionStorage.getItem(SESSION_STORAGE_KEYS.MEMBER)
      } catch (storageError) {
        // SecurityError: sessionStorage access denied
        console.warn('sessionStorage access failed:', storageError)
      }

      if (member) {
        let parseData: { id?: string | number; email?: string; name?: string } | null = null
        try {
          parseData = JSON.parse(member)
        } catch {
          // JSONパースエラーの場合はクリアして続行
          try {
            sessionStorage.removeItem('member')
          } catch {
            // sessionStorageアクセス失敗は無視
          }
        }

        if (parseData && typeof parseData === 'object' && parseData.id) {
          // 認証ありの場合、最小限のUser情報を設定
          const user: User = {
            id: typeof parseData.id === 'string' ? parseInt(parseData.id, 10) : parseData.id,
            email: parseData.email || '',
            name: parseData.name || '',
            isAuthenticated: true,
          }
          dispatch(setUser(user))
          dispatch(setLoading(false))
          setIsInitialized(true)
          isAuthCheckInProgress = false
        } else {
          dispatch(clearUser())
          dispatch(setLoading(false))
          setIsInitialized(true)
          isAuthCheckInProgress = false
        }
      } else {
        // ログアウト直後の場合はサーバー側認証チェックをスキップ
        const urlParams = new URLSearchParams(window.location.search)
        const isLogoutPage = urlParams.get('logout') === 'true'

        if (isLogoutPage) {
          dispatch(clearUser())
          dispatch(setLoading(false))
          setIsInitialized(true)
          isAuthCheckInProgress = false
          return
        }

        try {
          // ブラウザから直接Rails APIを呼び出す（Vue.jsと同じ方式）
          // これによりブラウザのRailsドメインCookieが送信される
          const apiUrl = process.env.NEXT_PUBLIC_API_URL
          const authToken = process.env.NEXT_PUBLIC_AUTH_TOKEN_API_HEADERS
          const basicAuthUser = process.env.NEXT_PUBLIC_BASIC_AUTH_USER
          const basicAuthPassword = process.env.NEXT_PUBLIC_BASIC_AUTH_PASSWORD

          if (!apiUrl || !authToken) {
            dispatch(clearUser())
            dispatch(setLoading(false))
            setIsInitialized(true)
            isAuthCheckInProgress = false
            return
          }

          // Basic認証の有無で分岐
          const headers: Record<string, string> = {
            'Auth-Token-Api': authToken,
            'Content-Type': 'application/json',
          }

          // Basic認証が設定されている場合のみ追加
          if (basicAuthUser && basicAuthPassword) {
            const basicAuth = btoa(`${basicAuthUser}:${basicAuthPassword}`)
            headers.Authorization = `Basic ${basicAuth}`
          }

          const response = await fetch(`${apiUrl}/members/auth_chk`, {
            method: 'GET',
            headers,
            credentials: 'include', // ブラウザのCookieを送信
          })

          if (response.ok) {
            const authData = await response.json()

            // Vue.js と同じ形式: {status: 200, id: 3163}
            if (authData && authData.status === 200 && authData.id) {
              // OAuth認証成功 - sessionStorageに保存
              const memberData = {
                id: authData.id,
                email: authData.email || '',
                name: authData.name || '',
              }

              try {
                sessionStorage.setItem(SESSION_STORAGE_KEYS.MEMBER, JSON.stringify(memberData))
              } catch (storageError) {
                console.warn('sessionStorage.setItem failed:', storageError)
              }

              // Redux状態を更新
              const user: User = {
                id: typeof authData.id === 'string' ? parseInt(authData.id, 10) : authData.id,
                email: memberData.email,
                name: memberData.name,
                isAuthenticated: true,
              }
              dispatch(setUser(user))
              dispatch(setLoading(false))
              setIsInitialized(true)
              isAuthCheckInProgress = false
            } else {
              dispatch(clearUser())
              dispatch(setLoading(false))
              setIsInitialized(true)
              isAuthCheckInProgress = false
            }
          } else {
            dispatch(clearUser())
            dispatch(setLoading(false))
            setIsInitialized(true)
            isAuthCheckInProgress = false
          }
        } catch (serverError) {
          dispatch(clearUser())
          dispatch(setLoading(false))
          setIsInitialized(true)
          isAuthCheckInProgress = false
        }
      }
    } catch (error) {
      // JSONパースエラーの場合はクリア
      dispatch(clearUser())
      dispatch(setLoading(false))
      setIsInitialized(true)
      isAuthCheckInProgress = false
    }
  }, [dispatch, authCheckCompleted])

  // ログアウト
  const logout = useCallback(async () => {
    // Prevent multiple logout calls
    if (isLogoutInProgress) {
      return
    }
    isLogoutInProgress = true

    try {
      dispatch(setLoading(true))

      // Reset global auth check flag
      isAuthCheckInProgress = false

      // まず先にクライアント側の状態をクリア
      dispatch(clearUser())

      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem('member')
        } catch (storageError) {
          console.warn('sessionStorage.removeItem failed:', storageError)
        }
      }

      // Rails APIを直接呼び出す（Vue.jsと同じ方式）
      try {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL
        const authToken = process.env.NEXT_PUBLIC_AUTH_TOKEN_API_HEADERS
        const basicAuthUser = process.env.NEXT_PUBLIC_BASIC_AUTH_USER
        const basicAuthPassword = process.env.NEXT_PUBLIC_BASIC_AUTH_PASSWORD

        if (apiUrl && authToken) {
          // Basic認証の有無で分岐
          const baseHeaders: Record<string, string> = {
            'Auth-Token-Api': authToken,
          }

          // Basic認証が設定されている場合のみ追加
          if (basicAuthUser && basicAuthPassword) {
            const basicAuth = btoa(`${basicAuthUser}:${basicAuthPassword}`)
            baseHeaders.Authorization = `Basic ${basicAuth}`
          }

          // Get CSRF token first
          const csrfResponse = await fetch(`${apiUrl}/members/get_csrf_token`, {
            method: 'GET',
            headers: baseHeaders,
            credentials: 'include',
          })

          if (csrfResponse.ok) {
            const csrfData = await csrfResponse.json()

            // Call logout API
            await fetch(`${apiUrl}/members/logout`, {
              method: 'PATCH',
              headers: {
                ...baseHeaders,
                'X-CSRF-Token': csrfData.csrf_token || csrfData.csrfToken || '',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({}),
              credentials: 'include',
            })
          }
        }
      } catch (apiError) {
        console.warn('Logout API failed, but client state already cleared:', apiError)
      }

      // sessionStorageから不要なフラグを削除
      if (typeof window !== 'undefined') {
        try {
          // API401の際にsetされるフラグ削除
          sessionStorage.removeItem('unauthorized_error')
          // 退会完了時にsetされるフラグ削除
          sessionStorage.removeItem('quitMessage')
        } catch (storageError) {
          console.warn('sessionStorage cleanup failed:', storageError)
        }
      }

      // Nuxtと同様にログインページにリダイレクト
      window.location.href = '/login?logout=true'
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : 'Logout failed'))

      // エラーでもクライアント側の状態はクリアしてリダイレクト
      dispatch(clearUser())
      if (typeof window !== 'undefined') {
        try {
          sessionStorage.removeItem('member')
        } catch (storageError) {
          console.warn('sessionStorage.removeItem failed in error handler:', storageError)
        }
        window.location.href = '/login?logout=true'
      }
    } finally {
      isLogoutInProgress = false
    }
  }, [dispatch])

  // 初回マウント時の認証初期化（NuxtのcheckCookieミドルウェアと同等）
  useEffect(() => {
    // 期限切れのBEFORE_PATHをクリーンアップ
    cleanupExpiredBeforePath()
    initializeAuth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // 依存関係を空にして初回のみ実行

  // authCheckCompletedがtrueの場合、isInitializedもtrueにする
  useEffect(() => {
    if (authCheckCompleted && !isInitialized) {
      setIsInitialized(true)
    }
  }, [authCheckCompleted, isInitialized])

  return {
    currentUser,
    isAuthenticated,
    isLoading,
    isInitialized,
    error,
    getMemberId,
    logout,
    initializeAuth, // 手動で認証状態を初期化したい場合
  }
}
